/**
 * Cuidari imperative toast — round 4.
 *
 * The v4 audit found mark-paid / copy-link / toggle-saved feedback was a
 * bespoke fixed-position `<div>` re-implemented per surface (HomeDesktop,
 * BillsDesktop, HistoricoDesktop, NotificationsDesktop, ProfileDesktop, …) with
 * a glyph-only `×`, no focus management, and no consistent motion. This routes
 * ALL local mutation confirmations through ONE themed MUI Snackbar + Alert with
 * queueing, auto-hide, slide motion, and an optional Undo action.
 *
 * Server-driven Pheme toasts stay in `src/lib/messaging/ToastProvider.tsx`;
 * this is the client/imperative channel. Mounted in `RootLayout` so every
 * authed route can call `useCuidariToast()`.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import Slide, { type SlideProps } from '@mui/material/Slide'

type ToastSeverity = 'success' | 'error' | 'info' | 'warning'

interface ToastOptions {
  message: ReactNode
  severity?: ToastSeverity
  /** ms; default 4000 (6000 for errors). */
  duration?: number
  action?: { label: string; onClick: () => void }
}

interface ToastEntry extends Required<Pick<ToastOptions, 'message'>> {
  key: number
  severity: ToastSeverity
  duration: number
  action?: ToastOptions['action']
}

interface ToastApi {
  notify: (opts: ToastOptions) => void
  success: (message: ReactNode, opts?: Omit<ToastOptions, 'message' | 'severity'>) => void
  error: (message: ReactNode, opts?: Omit<ToastOptions, 'message' | 'severity'>) => void
  info: (message: ReactNode, opts?: Omit<ToastOptions, 'message' | 'severity'>) => void
}

const ToastContext = createContext<ToastApi | null>(null)

function SlideUp(props: SlideProps) {
  return <Slide {...props} direction="up" />
}

export function CuidariToastProvider({ children }: { children: ReactNode }) {
  const [, setQueue] = useState<ToastEntry[]>([])
  const [current, setCurrent] = useState<ToastEntry | null>(null)
  const [open, setOpen] = useState(false)
  const keyRef = useRef(0)

  // Promote the next queued toast once the current one has exited.
  const processQueue = useCallback(() => {
    setQueue((q) => {
      if (q.length === 0) return q
      const [next, ...rest] = q
      setCurrent(next)
      setOpen(true)
      return rest
    })
  }, [])

  const notify = useCallback(
    (opts: ToastOptions) => {
      const severity = opts.severity ?? 'info'
      const entry: ToastEntry = {
        key: (keyRef.current += 1),
        message: opts.message,
        severity,
        duration: opts.duration ?? (severity === 'error' ? 6000 : 4000),
        action: opts.action,
      }
      setCurrent((cur) => {
        if (cur) {
          // Something is showing — close it; queue the new one.
          setQueue((q) => [...q, entry])
          setOpen(false)
          return cur
        }
        setOpen(true)
        return entry
      })
    },
    [],
  )

  const api = useMemo<ToastApi>(
    () => ({
      notify,
      success: (message, opts) => notify({ ...opts, message, severity: 'success' }),
      error: (message, opts) => notify({ ...opts, message, severity: 'error' }),
      info: (message, opts) => notify({ ...opts, message, severity: 'info' }),
    }),
    [notify],
  )

  const handleClose = (_e?: unknown, reason?: string) => {
    if (reason === 'clickaway') return
    setOpen(false)
  }

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Snackbar
        key={current?.key}
        open={open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={current?.duration ?? 4000}
        onClose={handleClose}
        slots={{ transition: SlideUp }}
        slotProps={{ transition: { onExited: processQueue } }}
        sx={{ bottom: { xs: 88, md: 32 } }}
      >
        {current ? (
          <Alert
            variant="filled"
            severity={current.severity}
            onClose={handleClose}
            action={
              current.action ? (
                <button
                  type="button"
                  onClick={() => {
                    current.action?.onClick()
                    handleClose()
                  }}
                  style={{
                    background: 'rgba(255,255,255,0.18)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '4px 12px',
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  {current.action.label}
                </button>
              ) : undefined
            }
            sx={{ maxWidth: 480, alignItems: 'center' }}
          >
            {current.message}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </ToastContext.Provider>
  )
}

/**
 * Imperative toast. Safe no-op if the provider isn't mounted (e.g. some test
 * harnesses), so callers never need to null-check.
 */
export function useCuidariToast(): ToastApi {
  const ctx = useContext(ToastContext)
  return (
    ctx ?? {
      notify: () => {},
      success: () => {},
      error: () => {},
      info: () => {},
    }
  )
}
